import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageCircle, Mail } from "lucide-react";
import { getServiceFAQs } from "./ServiceFAQsData";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRegion } from "../../../regions/RegionContext";

const ServiceFAQs = ({ service = null, showAll = false }) => {
  const { region, config } = useRegion();
  const whatsappHref = `${config.contact.whatsappLink}?text=Hi, I have a question about Terces services.`;
  const serviceFAQs = getServiceFAQs(region);
  const [openIndex, setOpenIndex] = useState(null);
  const [selectedService, setSelectedService] = useState(
    service || Object.keys(serviceFAQs)[0]
  );

  // Moving between service detail pages changes the `service` prop without
  // remounting this component (same route, different :serviceId), so the state
  // initialiser above never re-runs. Keep the selection in sync with the prop —
  // otherwise the FAQs stay on the service picked on mount until a hard refresh.
  useEffect(() => {
    if (!service) return;
    setSelectedService(service);
    setOpenIndex(null);
  }, [service]);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const selectedFAQs = serviceFAQs[selectedService] || [];

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#1b7840] text-sm font-bold uppercase tracking-wider mb-6">
            Got Questions?
          </p>
          <div className="inline-block relative">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 leading-tight">
              Frequently Asked Questions
            </h2>
            <motion.svg
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
              width="240"
              height="12"
              viewBox="0 0 240 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <path
                d="M2 10C50 2, 190 2, 238 10"
                stroke="#1b7840"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
              />
            </motion.svg>
          </div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mt-8">
            {showAll
              ? "Find answers to common questions about all our services"
              : `Everything you need to know about ${selectedService}`}
          </p>
        </motion.div>

        {/* Service Selector (if not showing all) */}
        {!showAll && !service && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {Object.keys(serviceFAQs).map((serviceName) => (
              <button
                key={serviceName}
                onClick={() => {
                  setSelectedService(serviceName);
                  setOpenIndex(null);
                }}
                className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                  selectedService === serviceName
                    ? "bg-gradient-to-r from-[#1b7840] to-[#145c32] text-white shadow-lg shadow-[#1b7840]/20"
                    : "bg-white text-gray-700 hover:bg-[#F6F4EF] border border-gray-200 hover:border-[#1b7840]/30"
                }`}
              >
                {serviceName}
              </button>
            ))}
          </div>
        )}

        {/* FAQ List */}
        {showAll ? (
          // Show all services with their FAQs
          <div className="space-y-12">
            {Object.entries(serviceFAQs).map(([serviceName, questions]) => (
              <motion.div
                key={serviceName}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-gradient-to-b from-[#1b7840] to-[#145c32] rounded-full" />
                  {serviceName}
                </h3>
                <div className="space-y-4">
                  {questions.map((faq, index) => {
                    const globalIndex = `${serviceName}-${index}`;
                    const isOpen = openIndex === globalIndex;

                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl hover:border-[#1b7840]/30 transition-all duration-300"
                      >
                        <button
                          onClick={() => toggleFAQ(globalIndex)}
                          className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left hover:bg-[#F6F4EF]/50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 flex-1">
                            {faq.question}
                          </span>
                          <div
                            className={`w-9 h-9 rounded-xl bg-gradient-to-br from-[#1b7840]/10 to-[#145c32]/10 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                              isOpen ? "rotate-180 bg-gradient-to-br from-[#1b7840]/20 to-[#145c32]/20" : ""
                            }`}
                          >
                            {isOpen ? (
                              <Minus className="w-5 h-5 text-[#1b7840]" />
                            ) : (
                              <Plus className="w-5 h-5 text-[#1b7840]" />
                            )}
                          </div>
                        </button>

                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-6 pt-4 bg-gradient-to-b from-[#F6F4EF]/40 to-[#F6F4EF]/60 border-t border-gray-200">
                                <p className="text-gray-700 leading-relaxed text-base">
                                  {faq.answer}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          // Show single service FAQs
          <div className="space-y-4">
            {selectedFAQs.map((faq, index) => {
              const isOpen = openIndex === index;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl hover:border-[#1b7840]/30 transition-all duration-300"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left hover:bg-[#F6F4EF]/50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 flex-1">
                      {faq.question}
                    </span>
                    <div
                      className={`w-9 h-9 rounded-xl bg-gradient-to-br from-[#1b7840]/10 to-[#145c32]/10 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        isOpen ? "rotate-180 bg-gradient-to-br from-[#1b7840]/20 to-[#145c32]/20" : ""
                      }`}
                    >
                      {isOpen ? (
                        <Minus className="w-5 h-5 text-[#1b7840]" />
                      ) : (
                        <Plus className="w-5 h-5 text-[#1b7840]" />
                      )}
                    </div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-4 bg-gradient-to-b from-[#F6F4EF]/40 to-[#F6F4EF]/60 border-t border-gray-200">
                          <p className="text-gray-700 leading-relaxed text-base">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center bg-gradient-to-br from-[#F6F4EF] to-white rounded-2xl p-8 border-2 border-[#1b7840]/20 shadow-lg"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Still Have Questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Our team is here to help. Get in touch for personalized answers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#25D366] to-[#1EBE57] hover:from-[#20ba5a] hover:to-[#17a34a] text-white px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-[#25D366]/40 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </a>
            <Link
              to="/Contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1b7840] to-[#145c32] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-xl hover:shadow-[#1b7840]/30 transition-all transform hover:scale-105 justify-center"
            >
              <Mail className="w-5 h-5" />
              Contact Us
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceFAQs;